import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  InputGroup,
  Badge,
  Spinner,
  Alert,
} from 'react-bootstrap';
import { updateTask, Task, TaskStatus, JobEmploymentType } from '../../services/taskService';

interface EditTaskModalProps {
  show: boolean;
  task: Task | null;
  onHide: () => void;
  onTaskUpdated: (updatedTask: Task) => void;
}

const EditTaskModal: React.FC<EditTaskModalProps> = ({ show, task, onHide, onTaskUpdated }) => {
  // Form State - Initialized in useEffect when task changes
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<TaskStatus>('OPEN');
  const [skills, setSkills] = useState<string[]>([]);
  const [skillInput, setSkillInput] = useState('');
  const [isRemote, setIsRemote] = useState(false);
  // Location - read-only for now, based on task prop
  const [locationName, setLocationName] = useState('');
  // Task Specific
  const [deadline, setDeadline] = useState(''); // Use string for input type='datetime-local'
  const [estimatedHours, setEstimatedHours] = useState<number | '' >('');
  // Job Specific
  const [employmentType, setEmploymentType] = useState<JobEmploymentType | '' >('');
  const [duration, setDuration] = useState('');
  const [compensation, setCompensation] = useState('');

  // Submission State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Populate form when task prop changes (and modal is shown)
  useEffect(() => {
    if (task && show) {
      setTitle(task.title || '');
      setDescription(task.description || '');
      setStatus(task.status || 'OPEN');
      setSkills(task.skills || []);
      setIsRemote(task.isRemote || false);
      setLocationName(task.locationName || 'N/A'); // Display existing location
      
      // Task specific
      setDeadline(task.deadline ? new Date(task.deadline).toISOString().slice(0, 16) : ''); // Format for datetime-local
      setEstimatedHours(task.estimatedHours ?? '');
      
      // Job specific
      setEmploymentType(task.employmentType ?? '');
      setDuration(task.duration ?? '');
      setCompensation(task.compensation ?? '');

      setSkillInput(''); // Reset skill input
      setSubmitError(null); // Clear previous errors
    } else {
        // Reset form when modal is hidden or task is null
        setTitle('');
        setDescription('');
        setStatus('OPEN');
        setSkills([]);
        setIsRemote(false);
        setLocationName('');
        setDeadline('');
        setEstimatedHours('');
        setEmploymentType('');
        setDuration('');
        setCompensation('');
        setSkillInput('');
        setSubmitError(null);
    }
  }, [task, show]);

  // --- Skill Management Handlers (same as create form) ---
  const handleAddSkill = () => {
    const trimmedSkill = skillInput.trim();
    if (trimmedSkill && !skills.includes(trimmedSkill)) {
      setSkills([...skills, trimmedSkill]);
      setSkillInput('');
    }
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    setSkills(skills.filter(skill => skill !== skillToRemove));
  };

  const handleSkillInputKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault(); 
      handleAddSkill();
    }
  };

  // --- Form Submission --- 
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!task) return; // Should not happen if modal is shown correctly

    setSubmitError(null);
    setIsSubmitting(true);

    const updatedData: Partial<Omit<Task, 'id' | 'createdAt' | 'updatedAt' | 'ownerId' | 'owner' | 'type'>> = {
        title,
        description,
        status,
        skills,
        isRemote,
        // Location is not editable in this version, but we might send it if needed
        // locationName: !isRemote ? locationName : null, 
        // latitude: !isRemote ? task.latitude : null, // Use original lat/lon if needed
        // longitude: !isRemote ? task.longitude : null,
        
        // Conditional fields based on original task type
        ...(task.type === 'TASK' && { 
            deadline: deadline ? new Date(deadline).toISOString() : null,
            estimatedHours: estimatedHours ? Number(estimatedHours) : null,
        }),
        ...(task.type === 'JOB' && { 
            employmentType: employmentType || null,
            duration: duration || null,
            compensation: compensation || null,
        })
    };

    try {
      const updatedTaskResult = await updateTask(task.id, updatedData);
      onTaskUpdated(updatedTaskResult); // Pass updated task back to parent
      onHide(); // Close modal on success
    } catch (error: any) {
      console.error("Error updating task:", error);
      setSubmitError(error.message || 'Failed to update task. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        {/* Task type is not editable */} 
        <Modal.Title>Edit {task?.type === 'JOB' ? 'Job' : 'Task'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {submitError && <Alert variant="danger">{submitError}</Alert>}
        {task ? (
          <Form onSubmit={handleSubmit}>
             {/* Title (Not editable in this version, but shown) */}
            {/* <Form.Group className="mb-3" controlId="editTaskTitle">
              <Form.Label>Title</Form.Label>
              <Form.Control type="text" value={task.title} readOnly disabled />
            </Form.Group> */}
            
            {/* Using Row/Col might not be necessary if Title isn't editable */}
             <Form.Group className="mb-3" controlId="editTaskTitle">
                <Form.Label>Title</Form.Label>
                <Form.Control 
                    type="text" 
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                />
              </Form.Group>

            <Form.Group className="mb-3" controlId="editTaskDescription">
              <Form.Label>Description</Form.Label>
              <Form.Control 
                  as="textarea" 
                  rows={3} 
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required 
              />
            </Form.Group>
            
            <Form.Group className="mb-3" controlId="editTaskStatus">
                <Form.Label>Status</Form.Label>
                <Form.Select 
                    value={status}
                    onChange={(e) => setStatus(e.target.value as TaskStatus)}
                    required
                >
                  {/* Dynamically show statuses based on type? Or allow all? */}
                  <option value="OPEN">Open</option>
                  {task.type !== 'JOB' && <option value="ASSIGNED">Assigned</option>} {/* Only show Assigned for Tasks */} 
                  <option value="CLOSED">Closed</option>
                </Form.Select>
              </Form.Group>

            <Form.Group className="mb-3" controlId="editTaskSkills">
              <Form.Label>Required Skills</Form.Label>
              <div className="mb-2">
                {skills.map(skill => (
                  <Badge pill bg="primary" key={skill} className="me-2 mb-1 p-2" style={{ cursor: 'pointer' }} onClick={() => handleRemoveSkill(skill)} title={`Remove ${skill}`}>
                    {skill} &times;
                  </Badge>
                ))}
              </div>
              <InputGroup>
                <Form.Control 
                  type="text" 
                  placeholder="Add a skill" 
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  onKeyPress={handleSkillInputKeyPress}
                />
                <Button variant="outline-secondary" onClick={handleAddSkill}>Add</Button>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3" controlId="editIsRemote">
              <Form.Check 
                type="checkbox"
                label="This is a remote task/job"
                checked={isRemote}
                onChange={(e) => setIsRemote(e.target.checked)}
              />
            </Form.Group>

            {!isRemote && (
              <Form.Group className="mb-3" controlId="editLocationName">
                <Form.Label>Location</Form.Label>
                <Form.Control 
                  type="text" 
                  value={locationName} 
                  readOnly 
                  disabled 
                />
                 <Form.Text className="text-muted">
                  Location is based on original posting and cannot be edited here.
                </Form.Text>
              </Form.Group>
            )}

            {/* Conditional Task/Job Fields */} 
            {task.type === 'TASK' && (
               <Row>
                 <Col md={6}>
                   <Form.Group className="mb-3" controlId="editTaskDeadline">
                       <Form.Label>Deadline (Optional)</Form.Label>
                       <Form.Control 
                           type="datetime-local" 
                           value={deadline}
                           onChange={(e) => setDeadline(e.target.value)}
                       />
                   </Form.Group>
                 </Col>
                 <Col md={6}>
                   <Form.Group className="mb-3" controlId="editTaskEstimatedHours">
                       <Form.Label>Estimated Hours (Optional)</Form.Label>
                       <Form.Control 
                           type="number"
                           min="0"
                           value={estimatedHours}
                           onChange={(e) => setEstimatedHours(e.target.value === '' ? '' : Number(e.target.value))}
                       />
                   </Form.Group>
                 </Col>
               </Row>
            )}
            {task.type === 'JOB' && (
                <Row>
                    <Col md={4}>
                        <Form.Group className="mb-3" controlId="editJobEmploymentType">
                            <Form.Label>Employment Type</Form.Label>
                            <Form.Select 
                                value={employmentType}
                                onChange={(e) => setEmploymentType(e.target.value as JobEmploymentType | '')}
                            >
                            <option value="">Select...</option>
                            <option value="FULL_TIME">Full-time</option>
                            <option value="PART_TIME">Part-time</option>
                            <option value="CONTRACT">Contract</option>
                            <option value="FREELANCE">Freelance</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group className="mb-3" controlId="editJobDuration">
                            <Form.Label>Duration (Optional)</Form.Label>
                            <Form.Control 
                                type="text" 
                                value={duration}
                                onChange={(e) => setDuration(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group className="mb-3" controlId="editJobCompensation">
                            <Form.Label>Compensation (Optional)</Form.Label>
                            <Form.Control 
                                type="text" 
                                value={compensation}
                                onChange={(e) => setCompensation(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            )}
            
            <Modal.Footer className="px-0 pb-0">
                <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>Cancel</Button>
                <Button variant="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Saving...</> : 'Save Changes'}
                </Button>
            </Modal.Footer>
          </Form>
        ) : (
          <p>Loading task data...</p> // Show loading or error if task is null initially
        )}
      </Modal.Body>
    </Modal>
  );
};

export default EditTaskModal; 